import { getScaledValue } from "../../actions/calculations-actions";
import { cloneChamberRows } from "../../lib/chamber-row";
import { OUTLET_MANIFOLD_ID } from "../../lib/types";
import React from "react";
import { Group, Line } from "react-konva";
import { connect } from "react-redux";
import { getManifoldStructureByString, getManifoldSizing, getManifoldStructureCenterSeparation, ManifoldStructureType } from "../../lib/manifolds";
import ManifoldSelectors from "../../reducers/main-manifold-reducer";
import { ManifoldUtils } from "../../lib/manifolds/utils";
class Underdrain extends React.Component {
  render() {
    if (!this.props.hasUnderdrain) {
      return null;
    }

    try {
      const chamberRows = cloneChamberRows(this.props.chamberRows);
      const firstActiveChamberRow = chamberRows.find(chamberRow => chamberRow.getActiveCount());
      const lastActiveChamberRow = chamberRows.reverse().find(chamberRow => chamberRow.getActiveCount());

      let calculatedBedWidth;
      try {
        if (lastActiveChamberRow && firstActiveChamberRow) {
          calculatedBedWidth = this.calculatedBedWidth(lastActiveChamberRow.row - firstActiveChamberRow.row + 1);
        } else {
          return null;
        }
      } catch (exception) {
        console.warn("No active chamber rows");
        return null;
      }

      const upperRightChamber = firstActiveChamberRow.getFirstActiveChamber();
      const lowerRightChamber = firstActiveChamberRow.getLastActiveChamber();
      const upperLeftChamber = lastActiveChamberRow.getFirstActiveChamber();
      const lowerLeftChamber = lastActiveChamberRow.getLastActiveChamber();

      const checkForUnderdrain = (chamber, isUpper) => {
        return !!( chamber !== null && chamber !== undefined &&
            ( ( isUpper && chamber.upperCapActive && chamber.upperCapAccessory && chamber.upperCapAccessory.id === OUTLET_MANIFOLD_ID ) ||
                ( !isUpper && chamber.lowerCapActive && chamber.lowerCapAccessory && chamber.lowerCapAccessory.id === OUTLET_MANIFOLD_ID ) ) );
      };

      const isOnlyRow = lastActiveChamberRow.row === firstActiveChamberRow.row;
      let underdrains = [];

      if (isOnlyRow) {
        if (checkForUnderdrain(upperLeftChamber, true)) {
          underdrains.push(this.renderUnderdrain(lastActiveChamberRow, upperLeftChamber, calculatedBedWidth, false, true));
        } else if (checkForUnderdrain(upperRightChamber, true)) {
          underdrains.push(this.renderUnderdrain(firstActiveChamberRow, upperRightChamber, calculatedBedWidth, true, true));
        }

        if (checkForUnderdrain(lowerRightChamber, false)) {
          underdrains.push(this.renderUnderdrain(firstActiveChamberRow, lowerRightChamber, calculatedBedWidth, true, false));
        } else if (checkForUnderdrain(lowerLeftChamber, false)) {
          underdrains.push(this.renderUnderdrain(lastActiveChamberRow, lowerLeftChamber, calculatedBedWidth, false, false));
        }
      } else {
        if (checkForUnderdrain(upperRightChamber, true)) {
          underdrains.push(this.renderUnderdrain(firstActiveChamberRow, upperRightChamber, calculatedBedWidth, true, true));
        }

        if (checkForUnderdrain(lowerRightChamber, false)) {
          underdrains.push(this.renderUnderdrain(firstActiveChamberRow, lowerRightChamber, calculatedBedWidth, true, false));
        }

        if (checkForUnderdrain(lowerLeftChamber, false)) {
          underdrains.push(this.renderUnderdrain(lastActiveChamberRow, lowerLeftChamber, calculatedBedWidth, false, false));
        }

        if (checkForUnderdrain(upperLeftChamber, true)) {
          underdrains.push(this.renderUnderdrain(lastActiveChamberRow, upperLeftChamber, calculatedBedWidth, false, true));
        }
      }

      const internalOutlets = ManifoldSelectors.getManifoldsByPurpose('outlet').filter(m => m.isManifoldInner);
      if (internalOutlets) {
        // Undo the chamberRows being reversed up above
        chamberRows.reverse();

        internalOutlets.forEach(outlet => {
          const outletRow = chamberRows[outlet.letRow];
          if (outletRow && outletRow.chambers) {
            const outletChamber = outletRow.chambers[outlet.letColumn];
            underdrains.push(this.renderUnderdrain(
              outletRow,
              outletChamber,
              calculatedBedWidth,
              firstActiveChamberRow.row === outlet.letRow,
              outlet.isUpper,
              outlet
            ));
          }
        });
      }

      return underdrains;
    } catch (exception) {
      console.warn("There was a problem rendering the underdrain", exception);
    }

    return null;
  }

  renderUnderdrain(chamberRow, chamber, calculatedBedWidth, isFirstChamberRow, isUpper, manifold = null) {
    const underdrainSeparationDistance = this.computeUnderdrainSeparationDistance(this.props.stoneBedSeparationDistance);

    let exactManifoldSeparation;
    if (!manifold) {
      let structure = getManifoldStructureByString(isUpper ? chamber.upperCapAccessory.variant : chamber.lowerCapAccessory.variant, this.props.manifoldStructures);
      let manifoldSizing = getManifoldSizing(this.props.totalStorageProvided, this.props.chamberType);

      if (!manifoldSizing) {
        return null;
      }

      exactManifoldSeparation = this.props.getScaledValue(
        getManifoldStructureCenterSeparation(structure, manifoldSizing, this.props.manifoldPipeSizes)
      );
    } else {
      const pipeSizeData = ManifoldUtils.getManifoldPipeSizeData(manifold.manifoldSize[chamberRow.isIsolatorRow ? "isolatorStubSize" : "stubNominal"]);
      const initialSeparation = 1;
      const isNylo = manifold.assembly.find(a => a.structureType !== null).structureType === ManifoldStructureType.Nyloplast;
      const structureSize = isNylo
        ? manifold.manifoldSize.nyloplastStructureSize
        : manifold.manifoldSize.concreteStructureSize;
      let pipeSeparation = (structureSize / 12) / 2;

      const adapterSeparation = isNylo
        ? (pipeSizeData.adapterOffset + pipeSizeData.adapterC + 3.0) / 12
        : 0;

      exactManifoldSeparation = this.props.getScaledValue(initialSeparation + adapterSeparation + pipeSeparation);
    }

    let height = chamberRow.height;
    let scale = { x: 1, y: 1 };
    let x = chamberRow.pixelOffSet + chamber.x - this.getEndCapWidth() + this.getEndcapInstalledOffset() - exactManifoldSeparation;
    let y = chamberRow.y + chamber.y - underdrainSeparationDistance;
    let chambersToCross;
    let isGoingUp = (!manifold && !isUpper);

    if (manifold) {
        const firstActiveCol = chamberRow.getFirstActiveChamber().col;
        const lastActiveCol = chamberRow.getLastActiveChamber().col;
        let upperChambers = ( manifold.letColumn + ( isUpper ? -1 : 1 ) ) - firstActiveCol;
        let lowerChambers = lastActiveCol - ( manifold.letColumn + ( isUpper ? -1 : 1 ) );
        if (upperChambers > lowerChambers) {
            isGoingUp = true;
            chambersToCross = Math.abs(manifold.letColumn - firstActiveCol) + 1;
        } else {
            chambersToCross = Math.abs(lastActiveCol - manifold.letColumn) + 1;
        }
    } else {
      chambersToCross = chamberRow.getLastActiveChamber().col - chamberRow.getFirstActiveChamber().col + 1;
    }
    let width = (chambersToCross * this.getChamberWidth()) -
      ((chambersToCross - 1) * this.getChamberInstalledOffset()) +
      exactManifoldSeparation +
      (this.getEndCapWidth() * 2);

    if (!isFirstChamberRow) {
      y = chamberRow.y + chamber.y + chamber.height + underdrainSeparationDistance;
      scale.y = -1;
    }

    if ((!manifold && !isUpper) || (manifold && isGoingUp))
      scale.x = -1;

    if (manifold && ((isUpper && isGoingUp) || (!isUpper && !isGoingUp)))
      chambersToCross -= 1;

    const xFromLower = chamberRow.pixelOffSet + chamber.x + exactManifoldSeparation + this.getChamberWidth() + this.getEndCapWidth() - this.getEndcapInstalledOffset();

    const widthForUnderdrainThatCrossesOverTheManifold = (chambersToCross * this.getChamberWidth()) -
      ((chambersToCross - 1) * this.getChamberInstalledOffset()) -
      exactManifoldSeparation;

    if (!manifold && !isUpper) {
      x = xFromLower;
    } else if (manifold) {
      if (isGoingUp) {
        if (!isUpper) {
          x = xFromLower;
        } else {
          width = widthForUnderdrainThatCrossesOverTheManifold;
        }
      } else if (!isUpper) {
        x = xFromLower;
        width = widthForUnderdrainThatCrossesOverTheManifold;
      }
    }

    return (
      <Group key={`${chamber.row}:${chamber.col}:${isUpper ? "upper" : "lower"}`} x={x} y={y} scale={scale}>
        <Line stroke={"limegreen"} strokeWidth={this.props.getScaledValue(0.5)} points={[0, height / 2, exactManifoldSeparation, 0, width, 0]} />
      </Group>
    );
  }

  // from chamber bed
  getChamberHeight() {
    return this.props.getScaledValue(this.props.chamberType.width);
  }

  getChamberWidth() {
    return this.props.getScaledValue(this.props.chamberType.length);
  }

  getChamberInstalledOffset() {
    let width = this.getChamberWidth();
    let installedWidth = this.props.getScaledValue(this.props.chamberType.installedLength);

    return width - installedWidth;
  }

  getEndCapHeight() {
    return this.props.getScaledValue(this.props.chamberEndCapType.width);
  }

  getEndCapWidth() {
    return this.props.getScaledValue(this.props.chamberEndCapType.length);
  }

  getEndcapInstalledOffset() {
    let width = this.getEndCapWidth();
    let installedWidth = this.props.getScaledValue(this.props.chamberEndCapType.installedLength);

    return width - installedWidth;
  }

  getRowSpacing() {
    return this.props.getScaledValue(this.props.chamberRowSpacing);
  }

  calculatedBedLength(numOfChambers) {
    let chamberWidth = this.getChamberWidth();
    let chamberInstalledOffset = this.getChamberInstalledOffset();
    let endCapWidth = this.getEndCapWidth();
    let endcapInstalledOffset = this.getEndcapInstalledOffset();

    return (
      chamberWidth * numOfChambers +
      chamberInstalledOffset * (numOfChambers - 1) +
      (endCapWidth - endcapInstalledOffset) * 2
    );
  }

  calculatedBedWidth(numOfRows) {
    let chamberHeight = this.getChamberHeight();
    let rowSpacing = this.getRowSpacing();

    return numOfRows * chamberHeight + rowSpacing * (numOfRows - 1);
  }

  computeUnderdrainSeparationDistance(stoneBedSeparationDistance) {
    return this.props.getScaledValue(0.6);
  }
}

const mapStateToProps = state => {
  return {
    hasUnderdrain: state.calculations.underdrain,
    stoneBedSeparationDistance: state.stonebed.sepDistance,
    chamberRows: state.chamberbed.chamberRows,
    totalStorageProvided: state.calculations.totalStorageProvided,
    manifoldStructures: state.stonebed.manifoldStructures,
    manifoldPipeSizes: state.stonebed.manifoldPipeSizes,
    manifoldSeparations: state.calculations.manifoldSeparations,
    chamberType: state.globals.chamberType,
    chamberEndCapType: state.globals.chamberEndCapType,
    chamberRowSpacing: state.globals.chamberRowSpacing,
    manifolds: state.mainManifolds.manifolds
  };
};
export default connect(
  mapStateToProps,
  {
    getScaledValue
  }
)(Underdrain);



// WEBPACK FOOTER //
// ./src/components/Drawings/underdrain.js